/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Stack } from '@mui/material';
import type { BoxProps } from '@mui/material';
import React from 'react';
import FilterList from './FilterList';
import type { ListFilter } from './FilterList';
import SearchField from './SearchField';

export const DEFAULT_FILTER_HEIGHT = 85;

interface FilterProps extends BoxProps {
  activeLabelFilterCount?: number;
  filters: ListFilter[];
  onClickFilterChip?: (id: string, selected: boolean) => void;
  onSearch?: (value: string) => void;
  isAllSelected?: boolean;
  searchQuery?: string;
  showLabelFilterToggle?: boolean;
  isLabelFilterOpen?: boolean;
  onToggleLabelFilter?: () => void;
  showSearchField?: boolean;
}

const Filter = React.forwardRef<HTMLElement, FilterProps>(
  (
    {
      activeLabelFilterCount = 0,
      filters,
      onClickFilterChip = (): void => {},
      onSearch = (): void => {},
      isAllSelected,
      searchQuery,
      showLabelFilterToggle = false,
      isLabelFilterOpen = false,
      onToggleLabelFilter,
      showSearchField = true,
      ...props
    }: FilterProps,
    ref: React.Ref<HTMLElement>,
  ) => {
    return (
      <Box
        ref={ref}
        sx={{
          marginBottom: '8px',
          width: '100%',
          ...(Array.isArray(props.sx)
            ? Object.assign({}, ...props.sx)
            : props.sx),
        }}
      >
        <Stack spacing={1}>
          {showSearchField ? (
            <SearchField value={searchQuery} valueChanged={onSearch} />
          ) : null}
          <FilterList
            activeLabelFilterCount={activeLabelFilterCount}
            filters={filters}
            isAllSelected={isAllSelected}
            isLabelFilterOpen={isLabelFilterOpen}
            onChipClick={onClickFilterChip}
            onToggleLabelFilter={onToggleLabelFilter}
            showLabelFilterToggle
          />
        </Stack>
      </Box>
    );
  },
);

export default Filter;
